import {
  GitMergeIcon,
  GitMergeQueueIcon,
  GitPullRequestIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  ArchiveIcon,
} from '@primer/octicons-react'
import {getSessionStatusIconAttributes} from '../components/StatusIcon'
import type {ArtifactState} from '../types/artifact'
import type {SessionState} from '../types/session'
import type {UITask} from '../types/task'

export interface IconAttributes {
  icon: React.ComponentType | (() => React.ReactElement)
  color: string
  'data-testid': string
  marginTop?: string
}

export function getLeadingVisualIconAttributes(
  prState?: ArtifactState,
  prInMergeQueue: boolean = false,
  state?: SessionState,
  isArchived?: boolean,
  agentSlug?: UITask['agentSlug'],
): IconAttributes {
  // Check PR states first
  if (prState === 'merged') {
    return {
      icon: GitMergeIcon,
      color: 'var(--fgColor-done)' as const,
      'data-testid': 'icon-git-merge',
    }
  }

  if (prState === 'closed') {
    return {
      icon: GitPullRequestClosedIcon,
      color: 'var(--fgColor-closed)' as const,
      'data-testid': 'icon-git-pull-request-closed',
    }
  }

  if (prInMergeQueue) {
    return {
      icon: GitMergeQueueIcon,
      color: 'var(--fgColor-attention)' as const,
      'data-testid': 'icon-git-merge-queue',
    }
  }

  // Check if the task is archived next to not mask the PR state
  if (isArchived) {
    return {
      icon: ArchiveIcon,
      color: 'var(--fgColor-muted)' as const,
      'data-testid': 'icon-archive',
    }
  }

  if (state === 'in_progress' || state === 'queued' || state === 'waiting_for_user') {
    return getSessionStatusIconAttributes(state, agentSlug)
  }

  if (prState === 'draft') {
    return {
      icon: GitPullRequestDraftIcon,
      color: 'var(--fgColor-draft)' as const,
      'data-testid': 'icon-git-pull-request-draft',
    }
  }

  if (prState === 'open') {
    return {
      icon: GitPullRequestIcon,
      color: 'var(--fgColor-open)' as const,
      'data-testid': 'icon-git-pull-request',
    }
  }

  return getSessionStatusIconAttributes(state, agentSlug)
}

/**
 * Human-readable status label matching the icon chosen by
 * {@link getLeadingVisualIconAttributes}. The status icon alone conveys no text,
 * so consumers can render this as a visually hidden label to give assistive-tech
 * users the same state information sighted users get from the icon's shape/color.
 */
export function getLeadingVisualStatusLabel(
  // No default here: `undefined` means "no PR", matching how
  // getLeadingVisualIconAttributes treats an absent prState. Defaulting to
  // 'open' would make a no-PR session announce "Open" while its icon shows the
  // session-state glyph (e.g. a neutral dot for a completed session).
  prState?: string,
  prInMergeQueue: boolean = false,
  state?: SessionState,
  isArchived?: boolean,
): string {
  if (prState === 'merged') return 'Merged'
  if (prState === 'closed') return 'Closed'
  if (prInMergeQueue) return 'In merge queue'
  if (isArchived) return 'Archived'

  // Mirror getLeadingVisualIconAttributes' precedence exactly so the visually
  // hidden label always matches the rendered icon. In particular, the active
  // and open/draft-PR states must be checked BEFORE the terminal session
  // states, otherwise the common "session completed, PR still open" case shows
  // the green open-PR icon but announces "Done".
  if (state === 'in_progress' || state === 'queued' || state === 'waiting_for_user') {
    return getSessionStatusIconAttributes(state).label
  }
  if (prState === 'draft') return 'Draft'
  if (prState === 'open') return 'Open'
  return getSessionStatusIconAttributes(state).label
}
